import React, { useState, useEffect, useCallback } from "react";
import { fetchUsersByGSI, fetchUsersByUsernames, fetchUserProgressFromDB, fetchTotalUserCount, getCountOfUsersByLevel, saveUserProfileToDB, fetchUserProfileFromDB } from "./dynamoUtils";
import styles from "../css/CardCommunityProgress.module.css";
import data_program from "../data/data_program";
import badge1 from "../assets/img/badges/1_badge_rabbit.png";
import badge2 from "../assets/img/badges/2_badge_deer.png";
import badge3 from "../assets/img/badges/3_badge_wolf.png";
import badge4 from "../assets/img/badges/4_badge_horse.png";
import badge5 from "../assets/img/badges/5_badge_bear.png";
import badge6 from "../assets/img/badges/6_badge_gorilla.png";
import badge7 from "../assets/img/badges/7_badge_lion.png";
import logo from "../assets/img/logo_t4a_white.svg";
import { useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { fetchAllUsernamesFromDB } from "./dynamoUtils";
import ModalVideo from "./ModalVideo";
import { toYouTubeEmbed, looksLikeVideoFilePath } from "./dynamoUtils";
import AvatarImg from "./AvatarImg";
import { resolveAvatarUrl } from "./avatar"

const badges = [badge1, badge2, badge3, badge4, badge5, badge6, badge7];

function CardCommunityProgress({ data_user }) {

  const [showLoginPrompt, setShowLoginPrompt] = useState(false);
  const { user: loggedInUser, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [sortOption, setSortOption] = useState("Latest");
  const [favoritesOnly, setFavoritesOnly] = useState(false);
  const [selectedLevel, setSelectedLevel] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [showVideo, setShowVideo] = useState(false);
  const [videoUrl, setVideoUrl] = useState(null);
  const [videoKind, setVideoKind] = useState("youtube"); // 'youtube' | 'file'
  const [videoTitle, setVideoTitle] = useState("");       // nice header for the modal


  const closeVideo = () => {
    setShowVideo(false);
    setVideoUrl(null);
  };

  const [favorites, setFavorites] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [lastEvaluatedKey, setLastEvaluatedKey] = useState(null);
  const [allUsers, setAllUsers] = useState([]); // Renamed from 'users'
  const [currentPage, setCurrentPage] = useState(1); // New state for current page
  const [totalUserCount, setTotalUserCount] = useState(0);
  const usersPerPage = 5; // Define users per page
  const [allUsernames, setAllUsernames] = useState([]); // for dropdown list
  const [searchInput, setSearchInput] = useState("");   // what the user types
  const [suggestions, setSuggestions] = useState([]);   // filtered usernames
  const [selectedIndex, setSelectedIndex] = useState(-1); // for keyboard navigation

  const getCurrentLevelFromProgress = (progressData = []) => {
    const badgeImages = badges; // uses your existing badges array

    let firstIncompleteLevel = -1;

    for (let level = 0; level < 7; level++) {
      const levelTasks = data_program.filter((d) => d.level === `Level ${level + 1}`);

      const levelComplete = levelTasks.every((task) => {
        const userProgress = progressData.find((p) => p.progress_code === task.progress_code);
        const best = userProgress?.best ?? 0;
        const goal = task.goal ?? 0;
        if (!goal) return false;
        return Math.min((best / goal) * 100, 100) === 100;
      });

      if (!levelComplete) {
        firstIncompleteLevel = level;
        break;
      }
    }

    if (firstIncompleteLevel === -1) firstIncompleteLevel = 6; // all complete → Level 7

    return {
      levelNumber: firstIncompleteLevel + 1, // 1..7
      badgeSrc: badgeImages[firstIncompleteLevel],
    };
  };

  const openCommunityVideo = async (bestUrl, progressCode, username, displayName) => {
    const prettyName = displayName || username;
    const prog = data_program.find(p => p.progress_code === progressCode);
    const progressionLabel = prog?.progression ?? progressCode.toUpperCase();

    if (!bestUrl) {
      // No video: show your fallback popup with a nice title
      setVideoUrl(null);
      setShowVideo(true);
      setVideoTitle(`${prettyName} – ${progressionLabel}`);
      return;
    }

    // Resolve kind + URL
    let url = null;
    let kind = "file";

    const yt = toYouTubeEmbed(bestUrl);
    if (yt) {
      url = yt;
      kind = "youtube";
    } else if (looksLikeVideoFilePath(bestUrl)) {
      url = bestUrl;
      kind = "file";
    } else {
      // last-resort: quick HEAD check for video-ish content
      try {
        const res = await fetch(bestUrl, { method: "HEAD" });
        const ct = (res.headers.get("content-type") || "").toLowerCase();
        if (
          ct.startsWith("video/") ||
          ct.includes("application/vnd.apple.mpegurl") || // m3u8
          ct.includes("application/dash+xml")            // mpd
        ) {
          url = bestUrl;
          kind = "file";
        }
      } catch {
        // ignore
      }
    }

    if (!url) {
      // Couldn’t resolve to a playable URL → same “coming soon” behavior
      setVideoUrl(null);
      setShowVideo(true);
      setVideoTitle(`${prettyName} – ${progressionLabel}`);
      return;
    }

    setVideoUrl(url);
    setVideoKind(kind);
    setVideoTitle(`${prettyName} – ${progressionLabel}`);
    setShowVideo(true);
  };


  useEffect(() => {
    const loadAllUsernames = async () => {
      try {
        const names = await fetchAllUsernamesFromDB();

        // ✅ Normalize: support ["zarrar", ...] OR [{ username, displayName }, ...]
        const normalized = names.map((u) =>
          typeof u === "string"
            ? { username: u, displayName: null }
            : u
        );

        setAllUsernames(normalized);
      } catch (err) {
        console.error("Error fetching usernames:", err);
      }
    };
    loadAllUsernames();
  }, []);

  const loadUsers = useCallback(async (isNewQuery) => {
    setIsLoading(true);
    // On a new query, reset the users, key, and page number
    const keyForQuery = isNewQuery ? null : lastEvaluatedKey;

    const { users: fetchedUsers, lastEvaluatedKey: newKey } = await fetchUsersByGSI({
      sortOption,
      selectedLevel,
      usersPerPage: usersPerPage,
      lastEvaluatedKey: keyForQuery,
    });

    const usersWithProgress = await Promise.all(
      fetchedUsers.map(async (u) => ({
        ...u,
        progressData: await fetchUserProgressFromDB(u.username),
      }))
    );

    if (isNewQuery) {
      setAllUsers(usersWithProgress); // Replace all users
      setCurrentPage(1); // Reset to page 1
    } else {
      // When loading the next page, append to the cache and go to it
      setAllUsers(prev => [...prev, ...usersWithProgress]);
      setCurrentPage(prev => prev + 1); // Go to the newly fetched page
    }

    setLastEvaluatedKey(newKey);
    setIsLoading(false);
  }, [sortOption, selectedLevel, lastEvaluatedKey]); // Dependencies are correct

  useEffect(() => {
    // --- KEY CHANGE ---
    // If the 'favoritesOnly' filter is active, stop this effect.
    // The new dedicated effect below will handle fetching favorites.
    if (favoritesOnly) {
      setAllUsers([]); // Clear the list to prevent showing non-favorites momentarily
      return;
    }

    const loadInitialData = async () => {
      setIsLoading(true);
      let count = 0;

      if (selectedLevel) {
        console.log(`[Component] Getting count for Level ${selectedLevel}...`);
        count = await getCountOfUsersByLevel(selectedLevel);
      } else {
        console.log(`[Component] Getting global total user count...`);
        count = await fetchTotalUserCount();
      }
      setTotalUserCount(count);

      const { users: fetchedUsers, lastEvaluatedKey: newKey } = await fetchUsersByGSI({
        sortOption,
        selectedLevel,
        usersPerPage: usersPerPage,
        lastEvaluatedKey: null,
      });

      const usersWithProgress = await Promise.all(
        fetchedUsers.map(async (u) => ({
          ...u,
          progressData: await fetchUserProgressFromDB(u.username),
        }))
      );

      setAllUsers(usersWithProgress);
      setCurrentPage(1);
      setLastEvaluatedKey(newKey);
      setIsLoading(false);
    };

    loadInitialData();
  }, [sortOption, selectedLevel, usersPerPage, favoritesOnly]);

  useEffect(() => {
    // This effect only runs when the user wants to see their favorites.
    if (favoritesOnly) {
      const loadFavoriteUsers = async () => {
        setIsLoading(true);

        // Get an array of usernames from the 'favorites' state object.
        const favoriteUsernames = Object.keys(favorites).filter(
          (username) => favorites[username]
        );

        if (favoriteUsernames.length > 0) {
          // Fetch the full data for ALL favorite users from the backend.
          const { users: favoriteUsers } = await fetchUsersByUsernames(favoriteUsernames);

          // Sort the fetched favorites on the client side based on the current sortOption.
          const sortedFavorites = [...favoriteUsers].sort((a, b) => {
            if (sortOption === 'Greatest') {
              return (b.journey_percentage || 0) - (a.journey_percentage || 0);
            }
            // Default sort is 'Latest'
            const dateA = a.latest_update ? new Date(a.latest_update) : new Date(0);
            const dateB = b.latest_update ? new Date(b.latest_update) : new Date(0);
            return dateB - dateA;
          });

          setAllUsers(sortedFavorites);
        } else {
          // If the user has no favorites, ensure the list is empty.
          setAllUsers([]);
        }

        // When showing a complete list of favorites, there's no "next page" from the backend.
        setLastEvaluatedKey(null);
        setCurrentPage(1); // Always reset to the first page of favorites.
        setIsLoading(false);
      };

      loadFavoriteUsers();
    }
  }, [favoritesOnly, favorites, sortOption]);

  useEffect(() => {
    const loadUserFavorites = async () => {
      if (isAuthenticated && loggedInUser?.username) {
        // Directly fetch the logged-in user's profile to get their favorites list
        const userProfile = await fetchUserProfileFromDB(loggedInUser.username);
        if (userProfile && userProfile.favorites) {
          // Convert the array of favorite usernames into an object for easy lookup
          const userFavoritesObject = userProfile.favorites.reduce((acc, fav) => {
            acc[fav] = true;
            return acc;
          }, {});
          setFavorites(userFavoritesObject);
        }
      }
    };

    loadUserFavorites();
  }, [isAuthenticated, loggedInUser?.username]);

  const getProgressBarColor = (percentage) => {
    if (percentage < 25) return "#E74C3C"; // Red
    if (percentage >= 25 && percentage < 50) return "#E67E22"; // Orange
    if (percentage >= 50 && percentage < 75) return "#F1C40F"; // Yellow
    if (percentage >= 75 && percentage < 100) return "#3498DB"; // Blue
    return "#2ECC71"; // Green
  };

  const parseLevel = (progressCode) => {
    const level = parseInt(progressCode[1]); // Extract level number
    const subLevel = progressCode[2].charCodeAt(0) - "a".charCodeAt(0); // Convert 'a', 'b', 'c' to 0, 1, 2
    return { level, subLevel };
  };

  const compareLevels = (a, b) => {
    const levelA = parseLevel(a.progress_code);
    const levelB = parseLevel(b.progress_code);
    if (levelA.level === levelB.level) {
      return levelB.subLevel - levelA.subLevel; // Higher sub-levels first
    }
    return levelB.level - levelA.level; // Higher main levels first
  };

  const getTopThreeLevels = (progressData) => {
    const nonZeroLevels = progressData.filter((item) => item.best > 0);
    const sortedLevels = nonZeroLevels.sort(compareLevels);
    return sortedLevels.slice(0, 3).reverse(); // Take the top 3 and reverse to flip the order
  };

  const getLevelColor = (progressCode) => {
    const levelNumber = parseInt(progressCode[1]); // Extract level number from "1a", "2b", etc.
    return levelNumber === 1 ? "black" : "white"; // Black for level 1, white for others
  };

  /*
  const calculateDurationInYears = (user) => {
    // No user or no first_upload → default 0
    if (!user || !user.first_upload) return "0";

    const firstDate = new Date(user.first_upload);
    if (Number.isNaN(firstDate.getTime())) {
      // first_upload is present but invalid (e.g. "")
      return "0";
    }

    let latestDate;

    if (user.latest_update) {
      const ld = new Date(user.latest_update);
      latestDate = Number.isNaN(ld.getTime()) ? firstDate : ld;
    } else if (Array.isArray(user.progressData) && user.progressData.length > 0) {
      // Fallback to latest valid best_date in progressData
      const bestDates = user.progressData
        .map((item) => (item.best_date ? new Date(item.best_date) : null))
        .filter((d) => d && !Number.isNaN(d.getTime()))
        .map((d) => d.getTime());

      latestDate = bestDates.length > 0 ? new Date(Math.max(...bestDates)) : firstDate;
    } else {
      // No latest_update and no valid best_date → just use firstDate
      latestDate = firstDate;
    }

    const durationInMs = Math.max(0, latestDate - firstDate);
    const durationInYears = durationInMs / (1000 * 60 * 60 * 24 * 365.25);

    // "0", "0.5", "1.2", but no "NaN"
    const years = durationInYears.toFixed(1).replace(/\.0$/, "");
    return years;
  };
*/

  // Add this new smaller block
  const filteredUsers = allUsers
    .filter((user) => {
      if (favoritesOnly) {
        return favorites[user.username] === true;
      }
      return true;
    })
    .filter((user) => {
      if (searchTerm) {
        return user.username.toLowerCase().includes(searchTerm.toLowerCase());
      }
      return true;
    });

  const actualTotalUsersForPagination = favoritesOnly || searchTerm ? filteredUsers.length : totalUserCount;
  const totalPages = Math.ceil(actualTotalUsersForPagination / usersPerPage);

  // Get the specific users for the current page from the filtered list
  const paginatedUsers = filteredUsers.slice(
    (currentPage - 1) * usersPerPage,
    currentPage * usersPerPage
  );

  return (
    <div className={styles.card}>
      <div className={styles.filterRow}>
        <button
          className={sortOption === "Latest" ? styles.active : ""}
          onClick={() => setSortOption("Latest")}
        >
          Latest
        </button>
        <button
          className={sortOption === "Greatest" ? styles.active : ""}
          onClick={() => setSortOption("Greatest")}
        >
          Greatest
        </button>
        <button
          className={favoritesOnly ? styles.active : ""}
          onClick={() => {
            setFavoritesOnly(prev => !prev);
            setCurrentPage(1);
          }}
        >
          ❤
        </button>
        <div style={{ position: "relative" }}>
          {searchTerm ? (
            // === When filtered: show reset button ===
            <button
              onClick={() => {
                setSearchInput("");
                setSearchTerm("");
                setSuggestions([]);
                setCurrentPage(1);
              }}
              className={styles.clearSearchButton}
            >
              All Users
            </button>
          ) : (
            // === When not filtered: show search input + dropdown ===
            <>
              <input
                type="text"
                placeholder="Search User"
                value={searchInput}
                onChange={(e) => {
                  const value = e.target.value;
                  setSearchInput(value);
                  setCurrentPage(1);
                  if (value.length > 0) {
                    setSuggestions(
                      allUsernames.filter((u) => {
                        const haystack = (u.displayName || u.username || "").toLowerCase();
                        return haystack.includes(value.toLowerCase());
                      })
                    );
                  } else {
                    setSuggestions([]);
                    setSearchTerm("");
                  }
                }}
                onKeyDown={(e) => {
                  if (e.key === "ArrowDown") {
                    setSelectedIndex((prev) =>
                      prev < suggestions.length - 1 ? prev + 1 : prev
                    );
                  } else if (e.key === "ArrowUp") {
                    setSelectedIndex((prev) => (prev > 0 ? prev - 1 : prev));
                  } else if (e.key === "Enter") {
                    if (selectedIndex >= 0 && selectedIndex < suggestions.length) {
                      const chosen = suggestions[selectedIndex];
                      const label = chosen.displayName || chosen.username;

                      setSearchInput(label);          // show pretty name
                      setSearchTerm(chosen.username); // filter by canonical username
                      setSuggestions([]);
                    } else {
                      // fallback: exact match by username or displayName
                      const match = allUsernames.find(
                        (u) =>
                          u.username === searchInput ||
                          u.displayName === searchInput
                      );
                      if (match) {
                        const label = match.displayName || match.username;
                        setSearchInput(label);
                        setSearchTerm(match.username);
                        setSuggestions([]);
                      }
                    }
                  }
                }}
                className={styles.searchInput}
                style={{ width: "150px" }}
              />
              {suggestions.length > 0 && (
                <ul className={styles.dropdownList}>
                  {suggestions.map((u, index) => (
                    <li
                      key={u.username}
                      className={
                        index === selectedIndex
                          ? styles.dropdownItemSelected
                          : styles.dropdownItem
                      }
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => {
                        const label = u.displayName || u.username;
                        setSearchInput(label);        // show mixed-case displayName
                        setSearchTerm(u.username);    // canonical filter key
                        setSuggestions([]);
                      }}
                    >
                      {u.displayName || u.username}
                    </li>
                  ))}
                </ul>
              )}
            </>
          )}
        </div>
        <div className={styles.dropdown}>
          <select
            value={selectedLevel || ""}
            onChange={(e) => {
              const level = e.target.value ? parseInt(e.target.value, 10) : null;
              setSelectedLevel(level);
              setCurrentPage(1);
            }}
          >
            <option value="">Level</option>
            {[...Array(7).keys()].map((level) => (
              <option key={level + 1} value={level + 1}>
                Level {level + 1}
              </option>
            ))}
          </select>
        </div>
      </div>
      <table className={styles.cardTable}>
        <thead>
          <tr>
            <th>User</th>
            <th>Level</th>
            <th>Journey</th>
            <th>Favorite</th>
            <th>Progress</th>
          </tr>
        </thead>
        <tbody>
          {paginatedUsers.map((user) => {
            const overallProgress = user.journey_percentage || 0;
            const { levelNumber, badgeSrc } = getCurrentLevelFromProgress(user.progressData);

            const topThreeLevels = getTopThreeLevels(user.progressData);

            return (
              <tr key={user.username}>
                {/* Cell 1: Avatar + Username */}
                <td
                  className={styles.user}
                  onClick={() => {
                    navigate(`/progress/${user.username}`);
                    sessionStorage.setItem('selectedUser', JSON.stringify(user));
                  }}
                  style={{ cursor: "pointer" }}
                >
                  <AvatarImg
                    src={resolveAvatarUrl(user.avatar)}
                    alt={`${user.displayName || user.username} Avatar`}
                    className={styles.avatar}
                  />
                  <span className={styles.username}>{user.displayName || user.username}</span>
                </td>

                {/* Cell 2: Badge + Level */}
                <td
                  className={styles.level}
                  onClick={() => {
                    navigate(`/progress/${user.username}`);
                    sessionStorage.setItem('selectedUser', JSON.stringify(user));
                  }}
                  style={{ cursor: "pointer" }}
                >
                  <img
                    src={badgeSrc}
                    alt={`Level ${levelNumber}`}
                    className={styles.badge}
                  />
                  <span>Level {levelNumber}</span>
                </td>

                {/* Cell 3: Journey Duration + Progress Bar */}
                <td
                  onClick={() => {
                    navigate(`/progress/${user.username}`);
                    sessionStorage.setItem('selectedUser', JSON.stringify(user));
                  }}
                  style={{ cursor: "pointer" }}
                >
                  <div className={styles.progressBarContainer}>
                    {/* <span className={styles.duration}>{calculateDurationInYears(user)} yr</span> */}
                    <div className={styles.progressBar}>
                      <div
                        className={styles.progressFill}
                        style={{
                          width: `${overallProgress}%`,
                          backgroundColor: getProgressBarColor(overallProgress),
                        }}
                      ></div>
                    </div>
                  </div>
                  <span className={styles.progressPercentage}>{Math.round(overallProgress)}%</span>
                </td>
                <td className={styles.favorite}>
                  <span
                    className={`${styles.heartIcon} ${favorites[user.username] ? styles.favorited : styles.unfavorited}`}
                    onClick={async (e) => {
                      // --- SOLUTION: Prevent default touch behavior & get the element ---
                      e.preventDefault();
                      const heartIcon = e.currentTarget;

                      if (!isAuthenticated || !loggedInUser?.username) {
                        setShowLoginPrompt(true);
                        return;
                      }

                      // Temporarily disable further clicks to prevent race conditions
                      heartIcon.style.pointerEvents = 'none';

                      const isFavorited = !!favorites[user.username];
                      const updatedFavorites = { ...favorites, [user.username]: !isFavorited };
                      setFavorites(updatedFavorites);

                      const favoritesArray = Object.keys(updatedFavorites).filter(
                        (key) => updatedFavorites[key] === true
                      );

                      try {
                        await saveUserProfileToDB(loggedInUser.username, { favorites: favoritesArray });
                        const userProfile = await fetchUserProfileFromDB(loggedInUser.username);
                        if (userProfile && userProfile.favorites) {
                          const userFavoritesObject = userProfile.favorites.reduce((acc, fav) => {
                            acc[fav] = true;
                            return acc;
                          }, {});
                          setFavorites(userFavoritesObject);
                        }
                      } catch (error) {
                        console.error("Failed to save favorites:", error);
                        setFavorites(favorites); // Revert on failure
                        alert("Could not save favorite. Please try again.");
                      } finally {
                        // --- SOLUTION: Remove focus from the element ---
                        heartIcon.blur();
                        heartIcon.style.pointerEvents = 'auto'; // Re-enable pointer events
                      }
                    }}
                  >
                    ❤
                  </span>
                </td>
                <td className={styles.levels}>
                  <div className={styles.levelsContainer}>
                    {topThreeLevels.map((level) => {
                      const shortLevel = level.progress_code.substring(1); // Remove the "l" prefix
                      const levelColor =
                        data_program.find((prog) => prog.progress_code === level.progress_code)?.color || "#ccc";

                      return (
                        <span
                          key={level.progress_code}
                          className={styles.levelCircle}
                          style={{
                            backgroundColor: levelColor,
                            color: getLevelColor(level.progress_code),
                            border: "1px solid black",
                            cursor: "pointer",
                          }}
                          onClick={() =>
                            openCommunityVideo(
                              level.best_url,
                              level.progress_code,
                              user.username,
                              user.displayName
                            )
                          }
                        >
                          {shortLevel}
                        </span>
                      );
                    })}
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <div className={styles.pagination}>
        {isLoading && <div>Loading...</div>}
        <button
          className={styles.paginationButton}
          onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
          disabled={currentPage === 1 || isLoading}
        >
          Previous
        </button>

        <span className={styles.paginationInfo}>
          Page {currentPage} of {totalPages > 0 ? totalPages : 1}
        </span>

        <button
          className={styles.paginationButton}
          onClick={() => {
            const isNextPageCached = (currentPage * usersPerPage) < allUsers.length;

            if (isNextPageCached) {
              setCurrentPage(prev => prev + 1);
            } else {
              // Only load more users from backend if NO client-side filters are active
              if (lastEvaluatedKey && !favoritesOnly && !searchTerm) {
                loadUsers(false);
              }
            }
          }}
          // The disabled logic is now simpler: disable if we are on the last
          // possible page AND there's no more data on the backend.
          disabled={(currentPage >= totalPages && (favoritesOnly || searchTerm || !lastEvaluatedKey)) || isLoading}        >
          Next
        </button>
      </div>

      {showLoginPrompt && (
        <div className={styles.videoPopup}>
          <div className={styles.videoCloseButton} onClick={() => setShowLoginPrompt(false)}>
            &times;
          </div>
          <a className="home-link" href="/login">Login Required</a>
          <img src={logo} alt="thenics4all Logo" className={styles.logo} />
          <div className={styles.comingSoonMessageSubtitle}>
            Please{' '}
            <span
              onClick={() => navigate('/login')}
              style={{ cursor: 'pointer', textDecoration: 'underline' }}
              role="link"
            >
              Login
            </span>{' '}
            to save favorites.
          </div>
        </div>
      )}

      {showVideo && (
        videoUrl ? (
          <ModalVideo
            show={showVideo}
            videos={[{ url: videoUrl, kind: videoKind, username: "", date: "", source: "community" }]}
            currentIndex={0}
            setCurrentIndex={() => { }}
            onClose={closeVideo}
            currentTitle={videoTitle}
            hideSubtitles
          />
        ) : (
          <div className={styles.videoPopup}>
            <div className={styles.videoCloseButton} onClick={closeVideo}>
              &times;
            </div>
            <div className={styles.comingSoonMessage}>Coming soon!</div>
            <img src={logo} alt="thenics4all Logo" className={styles.logo} />
            <div className={styles.comingSoonMessageSubtitle}>
              Video not ready yet!
            </div>
          </div>
        )
      )}

    </div>
  );

}

export default CardCommunityProgress;